import { useQuery } from "@tanstack/react-query";
import { Check, Minus, Pencil } from "lucide-react";
import { Fragment, useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import { Card, ErrorState, LoadingState, PageHeader } from "../../shared/ui";
import { RolePermissionsDrawer } from "./RolePermissionsDrawer";
import type { PermissionView, RoleView } from "../../shared/types/api";

/**
 * The role and permission matrix (§12).
 *
 * <p>The whole model on one screen. An administrator needs to answer "why can this person do
 * that" without reading the database, and a permission model nobody can inspect is one nobody can
 * reason about.
 *
 * <p>Holders of ROLE_MANAGE edit a role by clicking its column heading. Everyone else sees the
 * same table, read-only — the information is worth showing regardless of who may change it.
 */
export function RolesPage() {
  const { can } = useAuth();
  const [editing, setEditing] = useState<RoleView | null>(null);
  const manage = can("ROLE_MANAGE");

  const roles = useQuery({
    queryKey: ["roles"],
    queryFn: () => api.get<RoleView[]>("/api/v1/roles"),
  });

  const permissions = useQuery({
    queryKey: ["permissions"],
    queryFn: () => api.get<PermissionView[]>("/api/v1/permissions"),
  });

  if (roles.isPending || permissions.isPending) {
    return <LoadingState label="Loading roles…" />;
  }
  if (roles.isError) {
    return <ErrorState error={roles.error} onRetry={() => void roles.refetch()} />;
  }
  if (permissions.isError) {
    return <ErrorState error={permissions.error} onRetry={() => void permissions.refetch()} />;
  }

  const byCategory = permissions.data.reduce<Record<string, PermissionView[]>>(
    (accumulator, permission) => {
      (accumulator[permission.category] ??= []).push(permission);
      return accumulator;
    },
    {},
  );

  return (
    <>
      <PageHeader
        title="Roles & permissions"
        subtitle={
          manage
            ? "A role is a named bundle of permissions. Every endpoint checks the permission, not the role, so this table is the whole picture. Click a role to change what it grants."
            : "A role is a named bundle of permissions. Every endpoint checks the permission, not the role, so this table is the whole picture."
        }
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-ink-100 bg-ink-50">
                <th className="min-w-[240px] px-4 py-3 font-medium text-ink-500">Permission</th>
                {roles.data.map((role) => (
                  <th
                    key={role.id}
                    title={role.description ?? role.name}
                    className="min-w-[104px] px-3 py-3 text-center text-xs font-medium text-ink-500"
                  >
                    {manage ? (
                      <button
                        type="button"
                        onClick={() => setEditing(role)}
                        className="group inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:bg-ink-200 hover:text-ink-900"
                      >
                        {role.code.replace(/_/g, " ")}
                        <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                      </button>
                    ) : (
                      role.code.replace(/_/g, " ")
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(byCategory).map(([category, items]) => (
                // A keyed Fragment: each iteration emits a category header row plus its permission
                // rows, and without the key React cannot tell the groups apart.
                <Fragment key={category}>
                  <tr>
                    <td
                      colSpan={roles.data.length + 1}
                      className="bg-ink-100/70 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-500"
                    >
                      {category}
                    </td>
                  </tr>
                  {items.map((permission) => (
                    <tr key={permission.code} className="border-b border-ink-100/70 hover:bg-ink-50">
                      <td className="px-4 py-2.5" title={permission.description ?? ""}>
                        <span className="font-mono text-xs text-ink-700">{permission.code}</span>
                      </td>
                      {roles.data.map((role) => (
                        <td key={role.id} className="px-3 py-2.5 text-center">
                          {role.permissions.includes(permission.code) ? (
                            <Check className="mx-auto h-4 w-4 text-emerald-600" />
                          ) : (
                            <Minus className="mx-auto h-3.5 w-3.5 text-ink-300" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <RolePermissionsDrawer
        role={editing}
        permissions={permissions.data}
        onClose={() => setEditing(null)}
        onSaved={() => void roles.refetch()}
      />
    </>
  );
}
